import React, { useState, useEffect } from 'react';
import * as msal from '@azure/msal-browser';
import { useRouteMatch, useHistory } from 'react-router-dom';
import RoutePaths from 'RoutePaths';
import { LanguageStorageService } from 'localization/LanguageStorageService';
import { AuthService } from './AuthService';
import { B2cPolicies, StatsforvalterenDomainHint } from './AzureB2cConfig';
import { MsalNavigationClient } from './MsalNavigationClient';
import { IdTokenClaims } from './IdTokenClaims';
import { preserveAccountReference } from './AccountState';
import {
  changeSigninEmailErrorParameter,
  consumeChangeSigninEmailStarted,
  emailAlreadyInUseError,
  hasChangeSigninEmailFailed,
  hasChangeSigninEmailSucceeded
} from './ChangeSigninEmailState';

export interface IMsalContext {
  isAuthenticated: boolean;
  user: msal.AccountInfo | undefined;
  b2cAuthority: string | undefined;
  login: () => Promise<void>;
  resetPassword: (uiLocales?: string | undefined) => Promise<void>;
  changeSigninEmail: (uiLocales?: string | undefined) => Promise<void>;
  editPhoneNumber: (uiLocales?: string | undefined) => Promise<void>;
  logout: () => void;
  setB2cPolicy: (policyName: string) => void;
  isB2cPolicySet: () => boolean;
  ensureInternalUserLogin: () => Promise<void>;
  ensureStatsforvalterenLogin: () => Promise<void>;
  authInProgress: boolean;
}

export const MsalContext = React.createContext<IMsalContext | undefined>(undefined);

export const useMsal = () => {
  const context = React.useContext(MsalContext);
  if (context === undefined) {
    throw new Error('useMsal must be used within a MsalProvider');
  }
  return context;
};

export const MsalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<msal.AccountInfo | undefined>();
  const passwordResetMatch = useRouteMatch(RoutePaths.passwordReset);
  // authInProgress starter som true slik at vi kan vise lasting så tidlig som mulig,
  // men er false fra start for passordbytte (ingen autentisering)
  const [authInProgress, setAuthInProgress] = useState<boolean>(!passwordResetMatch);
  const isPasswordReset = !!passwordResetMatch;
  const [wasPasswordReset, setWasPasswordReset] = useState(isPasswordReset);
  if (wasPasswordReset !== isPasswordReset) {
    setWasPasswordReset(isPasswordReset);
    if (isPasswordReset) setAuthInProgress(false);
  }
  // Sett default til externalUser authority for å unngå "B2C policy is not set" feil
  // når bruker klikker login før HANDLE_REDIRECT_END har fyrt
  const [b2cAuthority, setB2cAuthority] = useState<string | undefined>(B2cPolicies.authorities.externalUser.authority);
  const { push, replace } = useHistory();
  AuthService.setNavigationClient(new MsalNavigationClient(push));

  const onMsalEvent = (event: msal.EventMessage) => {
    if (event.eventType === msal.EventType.HANDLE_REDIRECT_END) {
      if (!AuthService.isLoggedIn()) {
        setB2cAuthority(B2cPolicies.authorities.externalUser.authority);
      } else {
        setB2cAuthority(AuthService.mapHomeAccountIdToAuthority(AuthService.getActiveAccount()?.homeAccountId));
      }

      setUser(AuthService.getActiveAccount());
      setIsAuthenticated(AuthService.isLoggedIn());
    }

    if (event.eventType === msal.EventType.ACQUIRE_TOKEN_FAILURE) {
      setIsAuthenticated(AuthService.isLoggedIn());
      // Behold default authority for å unngå "B2C policy is not set" feil ved neste login-forsøk
      // Ikke sett til undefined, men behold eksisterende eller sett til default
      setB2cAuthority((prev) => prev ?? B2cPolicies.authorities.externalUser.authority);
      setUser(undefined);
    }

    if (event.eventType === msal.EventType.ACQUIRE_TOKEN_SUCCESS) {
      setUser((currentUser) => preserveAccountReference(currentUser, AuthService.getActiveAccount()));
      setIsAuthenticated(AuthService.isLoggedIn());
      if (AuthService.isLoggedIn())
        setB2cAuthority(AuthService.mapHomeAccountIdToAuthority(AuthService.getActiveAccount()?.homeAccountId));
    }
  };

  useEffect(() => {
    const authenticateAsync = async () => {
      AuthService.setMsalCallback((event: msal.EventMessage) => {
        onMsalEvent(event);
      });
      // Må leses før handleRedirect, som fjerner parametrene fra url-en.
      const changeSigninEmailStarted = consumeChangeSigninEmailStarted();
      const changeSigninEmailFailed =
        changeSigninEmailStarted && hasChangeSigninEmailFailed(window.location.search, window.location.hash);
      const changeSigninEmailSucceeded =
        changeSigninEmailStarted && hasChangeSigninEmailSucceeded(window.location.search, window.location.hash);

      let signOutStarted = false;

      try {
        setAuthInProgress(true);

        // E-postadressen er byttet, og da skal brukeren logges helt ut - også av B2C-sesjonen -
        // slik teksten i flyten sier. Men vi logger ikke ut herfra: en utlogging startet midt i
        // oppstarten av appen blir avbrutt av innloggingen MsalAuthWrapper starter så snart
        // authInProgress slippes, og med B2C-sesjonen i live blir brukeren logget rett inn igjen.
        // I stedet sender vi brukeren til bekreftelsessiden, som logger ut på et klikk - samme
        // mønster som passordbytte, den eneste av disse flytene som har virket.
        if (changeSigninEmailSucceeded) {
          replace(RoutePaths.changeSigninEmail);
          return;
        }

        const authResponse = await AuthService.handleRedirect();
        const callbackAccount = authResponse?.account ?? AuthService.getActiveAccount();

        if (AuthService.isChangeSigninEmailAccount(callbackAccount)) {
          await AuthService.signOut(RoutePaths.postLogout);
          signOutStarted = true;
          return;
        }

        if (AuthService.isEditPhoneNumberAccount(callbackAccount)) {
          await AuthService.signOut(RoutePaths.postLogout);
          signOutStarted = true;
          return;
        }
      } catch (e) {
        console.error('HandleRedirect: ', e);
      } finally {
        // En varsling her rakk brukeren knapt å lese før appen sendte dem videre til profilsiden,
        // så profilsiden viser i stedet feilen i en dialog. Vi navigerer først etter
        // handleRedirect, som trenger feilparametrene i url-en for å avslutte interaksjonen.
        if (changeSigninEmailFailed)
          replace(`${RoutePaths.profile}?${changeSigninEmailErrorParameter}=${emailAlreadyInUseError}`);

        // signOut har lagt en navigasjon til B2C sitt end-session-endepunkt i kø, men
        // MsalNavigationClient resolver med en gang den har satt window.location, så koden her
        // fortsetter mens nettleseren ennå står på siden. Slipper vi authInProgress nå, ser
        // MsalAuthWrapper en uinnlogget bruker og starter en ny innlogging som overskriver
        // utloggingen. B2C-sesjonen lever fortsatt, så brukeren blir logget rett inn igjen -
        // og står igjen innlogget etter e-postbyttet.
        if (!signOutStarted) setAuthInProgress(false);
      }
    };

    // skip handling authentication for password reset route
    if (!passwordResetMatch) {
      authenticateAsync();
    }
    // replace er stabil gjennom hele appens levetid, så den utløser ingen ny kjøring.
  }, [passwordResetMatch, replace]);

  const setB2cPolicy = (policyName: string) => {
    const authority = mapPolicyNameToAuthority(policyName);

    setB2cAuthority(authority);
  };

  const ensureInternalUserLogin = async () => {
    if (authInProgress) {
      console.log('ensureInternalUserLogin skipped - authentication already in progress');
      return;
    }

    const currentUserAuthority = AuthService.mapHomeAccountIdToAuthority(AuthService.getActiveAccount()?.homeAccountId);
    const isInternalUser = B2cPolicies.authorities.internalUser.authority === currentUserAuthority;

    if (!isInternalUser && isAuthenticated) {
      await AuthService.signOut(RoutePaths.loginInternal);
      return;
    }

    if (!isInternalUser) {
      setAuthInProgress(true);
      await AuthService.signInRedirect(
        B2cPolicies.authorities.internalUser.authority,
        RoutePaths.home,
        undefined,
        undefined,
        LanguageStorageService.getPreferredLanguageOrDefault().shortName
      );
    } else {
      replace(RoutePaths.home);
    }
  };

  const ensureStatsforvalterenLogin = async () => {
    if (authInProgress) {
      console.log('ensureStatsforvalterenLogin skipped - authentication already in progress');
      return;
    }

    const isStatsforvalteren = (user?.idTokenClaims as IdTokenClaims)?.idp_code === 'Statsforvalteren';

    if (!isStatsforvalteren && isAuthenticated) {
      await AuthService.signOut(RoutePaths.loginStatsforvalteren);
      return;
    }

    if (!isStatsforvalteren) {
      setAuthInProgress(true);
      await AuthService.signInRedirect(
        B2cPolicies.authorities.externalUser.authority,
        RoutePaths.home,
        StatsforvalterenDomainHint,
        true,
        LanguageStorageService.getPreferredLanguageOrDefault().shortName
      );
    } else {
      replace(RoutePaths.home);
    }
  };

  const mapPolicyNameToAuthority = (policyName: string) => {
    if (policyName === B2cPolicies.names.internalUser) return B2cPolicies.authorities.internalUser.authority;
    if (policyName === B2cPolicies.names.externalUser) return B2cPolicies.authorities.externalUser.authority;

    throw Error(`${policyName} is not a valid B2C policy name.`);
  };

  const isB2cPolicySet = () => {
    return b2cAuthority !== undefined;
  };

  const login = async () => {
    // Forhindre "interaction_in_progress" feil ved å sjekke om auth allerede pågår
    if (authInProgress) {
      console.log('Login skipped - authentication already in progress');
      return;
    }

    if (b2cAuthority === undefined) throw Error(`B2C policy is not set.`);

    setAuthInProgress(true);
    await AuthService.signInRedirect(
      b2cAuthority,
      undefined,
      undefined,
      undefined,
      LanguageStorageService.getPreferredLanguageOrDefault().shortName
    );
  };

  const resetPassword = async (uiLocales: string | undefined) => {
    await AuthService.resetPassword(uiLocales ?? LanguageStorageService.getPreferredLanguageOrDefault().shortName);
  };

  const changeSigninEmail = async (uiLocales: string | undefined) => {
    await AuthService.changeSigninEmail(uiLocales ?? LanguageStorageService.getPreferredLanguageOrDefault().shortName);
  };

  const editPhoneNumber = async (uiLocales: string | undefined) => {
    await AuthService.editPhoneNumber(uiLocales ?? LanguageStorageService.getPreferredLanguageOrDefault().shortName);
  };

  const logout = async () => {
    if (authInProgress) {
      console.log('Logout skipped - authentication already in progress');
      return;
    }

    setAuthInProgress(true);
    await AuthService.signOut(RoutePaths.postLogout);
  };

  return (
    <MsalContext.Provider
      value={{
        isAuthenticated,
        user,
        b2cAuthority,
        authInProgress,
        login,
        resetPassword,
        changeSigninEmail,
        editPhoneNumber,
        logout,
        setB2cPolicy,
        isB2cPolicySet,
        ensureInternalUserLogin,
        ensureStatsforvalterenLogin
      }}
    >
      {children}
    </MsalContext.Provider>
  );
};
